import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Observable, Subject} from 'rxjs';
import {debounceTime, switchMap, take} from 'rxjs/operators';
import {ProviderDTO} from '../../../../ent/models/ProviderDTO';
import {ReferenceDTO} from '../../../models/application/ReferenceDTO';
import {NationalDrugDTO} from '../../../../pbm/models/NationalDrugDTO';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {NameValueCodeDTO} from '../../../../common/models/NameValueCodeDTO';
import {ProviderService} from '../../../../ent/services/provider.service';
import {ReferenceService} from '../../../services/reference.service';
import {NationalDrugService} from '../../../../pbm/services/national-drug.service';
import {ReferenceLookupValue} from '../../../../clm/types/reference-lookup-value.enum';
import {ApplicationConditionDTO} from '../../../models/application/ApplicationConditionDTO';
import {ApplicationUtil} from 'src/app/modules/obd/obd-common/utils/ApplicationUtil';
import {MatCheckboxChange} from '@angular/material/checkbox';

enum Controls {
  QuestionCode = 'questionCode',
  ConditionCode = 'conditionCode',
  Provider = 'provider',
  Medication = 'medication',
  FirstTreatmentDate = 'firstTreatmentDate',
  LastTreatmentDate = 'lastTreatmentDate',
  IsCurrent = 'isCurrent',
  ConditionFreeTextActive = 'conditionFreeTextActive',
}

@Component({
  selector: 'app-application-condition-dialog',
  templateUrl: './application-condition-dialog.component.html',
  styleUrls: [
    '../../../../../../styles/global/_dialog-styles.scss',
  ]
})
export class ApplicationConditionDialogComponent implements OnInit {
  readonly Controls = Controls;

  heading: string;
  conditionForm!: FormGroup;
  providers: ProviderDTO[] = [];
  questionCodes: ReferenceDTO[] = [];
  medications: NationalDrugDTO[] = [];
  conditionFreeTextActive = false;

  private providerSearchSubject: Subject<string> = new Subject<string>();
  private medicationSearchSubject: Subject<string> = new Subject<string>();

  constructor(
    private formBuilder: FormBuilder,
    private referenceService: ReferenceService,
    private providerService: ProviderService,
    private nationalDrugService: NationalDrugService,
    public dialogRef: MatDialogRef<ApplicationConditionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      condition?: ApplicationConditionDTO,
      diagnosisGroups: NameValueCodeDTO[]
    }
  ) {
    this.heading = data.condition ? 'Edit Condition' : 'Add Condition';
    this.initForm();
    if (data.condition) {
      this.setFormValues(data.condition);
    }
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.initReferenceValues();

    if (this.data.condition) {
      this.initEditModeDetails();
    }
  }

  initForm(): void {
    this.conditionForm = this.formBuilder.group({
      [Controls.QuestionCode]: ['', Validators.required],
      [Controls.ConditionCode]: ['', Validators.required],
      [Controls.Provider]: [''],
      [Controls.Medication]: [''],
      [Controls.FirstTreatmentDate]: ['', Validators.required],
      [Controls.LastTreatmentDate]: ['', Validators.required],
      [Controls.IsCurrent]: [this.data.condition?.isCurrent ?? true],
      [Controls.ConditionFreeTextActive]: false
    });
  }

  initReferenceValues(): void {
    this.referenceService.getAllByFieldName(ReferenceLookupValue.QuestionCode)
      .pipe(take(1))
      .subscribe({
        next: (response: ReferenceDTO[]) => {
          this.questionCodes = response;
        },
        error: (error) => {
          console.error('Error loading question codes:', error);
        }
      });
  }

  initSubscriptions(): void {
    this.initProviderSearchSubscription();
    this.initMedicationSearchSubscription();
  }

  initProviderSearchSubscription(): void {
    this.providerSearchSubject.pipe(
      debounceTime(300),
      switchMap((searchTerm: string) => this.searchProviders(searchTerm))
    ).subscribe({
      next: (response: any) => {
        this.providers = response.resultList;
      },
      error: (errorResponse: any) => {
        console.error('Error fetching providers:', errorResponse);
      }
    });
  }

  initMedicationSearchSubscription(): void {
    this.medicationSearchSubject.pipe(
      debounceTime(300),
      switchMap((searchTerm: string) => this.searchMedications(searchTerm))
    ).subscribe({
      next: (response: any) => {
        this.medications = response.resultList;
      },
      error: (errorResponse: any) => {
        console.error('Error fetching medications:', errorResponse);
      }
    });
  }

  onProviderSearch(searchTerm: string): void {
    this.providerSearchSubject.next(searchTerm);
  }

  onMedicationSearch(searchTerm: string): void {
    this.medicationSearchSubject.next(searchTerm);
  }

  setFormValues(condition: ApplicationConditionDTO): void {
     this.conditionFreeTextActive = ApplicationUtil.isFreeTextCondition(condition[Controls.ConditionCode],this.data?.diagnosisGroups);

    const formValues = {
      [Controls.QuestionCode]: condition[Controls.QuestionCode],
      [Controls.ConditionCode]: condition[Controls.ConditionCode],
      [Controls.Provider]: condition[Controls.Provider]?.providerCode,
      [Controls.Medication]: condition[Controls.Medication]?.drugCode,
      [Controls.FirstTreatmentDate]: condition[Controls.FirstTreatmentDate] ? new Date(condition[Controls.FirstTreatmentDate]) : null,
      [Controls.LastTreatmentDate]: condition[Controls.LastTreatmentDate] ? new Date(condition[Controls.LastTreatmentDate]) : null,
      [Controls.IsCurrent]: condition[Controls.IsCurrent],
      [Controls.ConditionFreeTextActive]: this.conditionFreeTextActive
    };

    this.conditionForm.patchValue(formValues);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getProviderDisplayString(providerId: number): string {
    if (!this.providers?.length || !providerId) {
      return;
    }
    const found: ProviderDTO = this.providers.find((p: ProviderDTO) => p.providerId === providerId);
    return `${found.practiceNumber}: ${found.name}`;
  }

  getMedicationDisplayString(nationalDrugId: number): string {
    if (!this.medications?.length || !nationalDrugId) {
      return;
    }
    const found: NationalDrugDTO = this.medications.find((m: NationalDrugDTO) => m.nationalDrugId === nationalDrugId);
    return `${found.nationalDrugCode}: ${found.brandName}`;
  }

  onSave(): void {
    if (this.conditionForm.valid) {
      const formValue = this.conditionForm.value;
      const selectedProvider: ProviderDTO | undefined = this.providers.find(p => p.providerId === formValue[Controls.Provider]);
      const selectedMedication: NationalDrugDTO | undefined = this.medications.find(m => m.nationalDrugId === formValue[Controls.Medication]);

      const condition: Partial<ApplicationConditionDTO> = {
        applicationConditionId: this.data.condition?.applicationConditionId || null,
        questionCode: formValue[Controls.QuestionCode],
        conditionCode: formValue[Controls.ConditionCode],
        isCurrent: formValue[Controls.IsCurrent],
        firstTreatmentDate: formValue[Controls.FirstTreatmentDate],
        lastTreatmentDate: formValue[Controls.LastTreatmentDate],
        provider: selectedProvider ? {
          applicationProviderId: selectedProvider.providerId,
          providerCode: selectedProvider.practiceNumber,
          providerName: selectedProvider.name,
          providerOrder: selectedProvider.providerOrder,
        } : null,
        medication: selectedMedication ? {
          applicationMedicationId: selectedMedication.nationalDrugId,
          drugCode: selectedMedication.nationalDrugCode,
          drugName: selectedMedication.brandName
        } : null
      };

      this.dialogRef.close(condition);
    }
  }

  onConditionFreeTextChange(event: MatCheckboxChange): void {
      this.conditionFreeTextActive = event.checked;
      this.conditionForm.get(Controls.ConditionCode)?.setValue('');
      this.conditionForm.get(Controls.ConditionCode)?.reset();
  }

  private searchProviders(searchTerm: string): Observable<any> {
    const request = new PagedRequestDTO();
    request.entityClass = 'TblProvider';
    request.pageNumber = 0;
    request.pageSize = 10;
    request.sortField = 'name';
    request.sortDirection = 'asc';

    const nameFilter = {name: 'name', value: searchTerm.trim()};
    const practiceNumberFilter = {name: 'practiceNumber', value: searchTerm.trim(), logicGate: 'OR'};
    request.fieldMap = {
      name: nameFilter,
      practiceNumber: practiceNumberFilter
    };

    return this.providerService.getListOfProviders(request);
  }

  private searchMedications(searchTerm: string): Observable<any> {
    const request = new PagedRequestDTO();
    request.entityClass = 'TblNationalDrug';
    request.pageNumber = 0;
    request.pageSize = 10;
    request.sortField = 'drugName';
    request.sortDirection = 'asc';

    const drugNameFilter = {name: 'drugName', value: searchTerm.trim()};
    const drugCodeFilter = {name: 'nationalDrugCode', value: searchTerm.trim(), logicGate: 'OR'};
    request.fieldMap = {
      brandName: drugNameFilter,
      nationalDrugCode: drugCodeFilter
    };

    return this.nationalDrugService.getListOfNationalDrugs(request);
  }

  private initEditModeDetails(): void {
    const condition = this.data.condition;
    if (!condition) {
      return;
    }

    const {provider, medication} = condition;

    if (provider?.providerCode) {
      this.searchProviders(provider.providerCode)
        .pipe(take(1))
        .subscribe(response => {
          this.providers = response.resultList;
          const found: ProviderDTO = this.providers.find(p => p.practiceNumber === provider.providerCode);
          if (found) {
            this.conditionForm.get(Controls.Provider)?.setValue(found.providerId);
          }
        });
    }

    if (medication?.drugCode) {
      this.searchMedications(medication.drugCode)
        .pipe(take(1))
        .subscribe(response => {
          this.medications = response.resultList;
          const found: NationalDrugDTO = this.medications.find(m => m.nationalDrugCode === medication.drugCode);
          if (found) {
            this.conditionForm.get(Controls.Medication)?.setValue(found.nationalDrugId);
          }
        });
    }
  }
}
